import Dispatch from '../core/Dispatch';
import { COMPONENT_INIT } from '../lib/events';
import { post } from '../lib/ajax';
import animatedScroll from '../lib/animatedScroll';
import Viewport from '../core/Viewport';

export default (form, options) => {

    const errorMessage = form.querySelector('[data-error]');
    const submitBtn = form.querySelector('button[type="submit"]');

    const announcer = form.querySelector('[data-announcer]');

    const messages = JSON.parse(form.dataset.announcements || '{}');

    const { onSuccess } = options || {};

    let isSubmitting = false;

    const disableSubmit = () => {
        submitBtn.setAttribute('aria-disabled', 'true');
    };

    const enableSubmit = () => {
        submitBtn.removeAttribute('aria-disabled');
    };

    // Clear input fields
    const clearInputs = () => {
        form.querySelectorAll('input:not([type="hidden"]),textarea,select').forEach(input => {
            if (input.nodeName === 'SELECT') {
                input.selectedIndex = 0;
            } else if (['radio', 'checkbox'].indexOf(input.getAttribute('type')) > -1) {
                input.checked = false;
            } else if (input._datepicker && input._datepicker.hasOwnProperty('clear')) {
                input.value = null;
                input._datepicker.clear();
            } else {
                input.value = null;
            }
            try {
                input.dispatchEvent(new Event('change'));
            } catch (error) {
                console.error(error);
            }
        });
    };

    const showGenericError = message => {
        errorMessage.hidden = false;
        errorMessage.firstElementChild.innerHTML = message;
        errorMessage.focus = true;
    };

    const clearErrors = () => {
        if (errorMessage) {
            errorMessage.hidden = true;
            errorMessage.firstElementChild.innerHTML = '';
        }
        form.querySelectorAll('[data-errors]').forEach(node => {
            node.innerHTML = '';
            node.hidden = true;
        });
        form.querySelectorAll('[data-input]').forEach(node => {
            node.classList.remove('error');
        });
    };

    const getErrorsByName = (errors, prefix = '') => Object.keys(errors || {}).reduce((carry, key) => {
        if (typeof errors[key] === 'object' && !Array.isArray(errors[key])) {
            return {
                ...carry,
                ...getErrorsByName(errors[key], key)
            };
        }
        const name = prefix ? `${prefix}[${key}]` : key;
        return {
            ...carry,
            [name]: errors[key]
        };
    }, {});

    const showErrors = (errors = []) => {

        clearErrors();

        const errorsByName = getErrorsByName(errors);
        const errorNames = Object.keys(errorsByName);

        if (!errorNames.length) {
            showGenericError();
            return;
        }

        errorNames.forEach(name => {
            const inputDiv = form.querySelector(`[data-input="${name}"]`);
            if (inputDiv) {
                inputDiv.classList.add('error');
            }
            const errorsDiv = form.querySelector(`[data-errors="${name}"]`);
            if (errorsDiv) {
                errorsDiv.hidden = false;
                errorsDiv.innerHTML = [].concat(errorsByName[name]).join('<br/>');
            }
        });

        const firstErrorInput = form.querySelector('[data-input].error input:not([type="hidden"]),textarea,select');
        if (firstErrorInput) {
            try {
                firstErrorInput.focus({ preventScroll: true });
                if (!Viewport.visible(firstErrorInput, -firstErrorInput.getBoundingClientRect().height)) {
                    animatedScroll(firstErrorInput);
                }
            } catch (error) {
                // Don't care.
            }
        }

        enableSubmit();

    };

    const hideReceipt = () => {
        const receipt = form.querySelector('[data-receipt]');
        if (!receipt || receipt.hidden) {
            return;
        }
        receipt.hidden = true;
    };

    const showReceipt = () => {
        const receipt = form.querySelector('[data-receipt]');
        if (!receipt) {
            return;
        }
        receipt.hidden = false;
    };

    const onSubmit = async e => {

        e.preventDefault();

        if (isSubmitting || submitBtn.hasAttribute('aria-disabled')) {
            return;
        }

        isSubmitting = true;
        disableSubmit();

        submitBtn.classList.add('loading');

        if (announcer) {
            announcer.textContent = messages.submitting || '';
        }

        const url = window.location.href.split('?')[0].split('#')[0];

        const response = await post(url, { body: new FormData(form) });

        const { status } = response;

        response.json()
            .then(res => {
                clearErrors();
                if (status < 400) {
                    if (announcer) {
                        announcer.textContent = messages.submitted || '';
                    }
                    clearInputs();
                    if (onSuccess) {
                        onSuccess(res);
                        return;
                    }
                    const { redirect } = res;
                    if (redirect) {
                        if (redirect.startsWith('http')) {
                            window.location.href = res.redirect;
                        } else {
                            window.location.href = `/${res.redirect}`;
                        }
                    } else {
                        showReceipt();
                    }
                } else if (res.errors) {
                    if (announcer) {
                        announcer.textContent = messages.errors || '';
                    }
                    showErrors(res.errors);
                } else {
                    if (announcer) {
                        announcer.textContent = messages.failure || '';
                    }
                    showGenericError(res.message);
                }
            })
            .finally(() => {
                isSubmitting = false;
                submitBtn.classList.remove('loading');
                enableSubmit();
            });
    };

    const onChange = () => {
        hideReceipt();
    };

    const init = () => {
        form.addEventListener('submit', onSubmit);
        form.addEventListener('change', onChange);
        form.addEventListener('keyup', onChange);

        Dispatch.emit(COMPONENT_INIT);
    };

    const destroy = () => {
        form.removeEventListener('submit', onSubmit);
        form.removeEventListener('change', onChange);
        form.removeEventListener('keyup', onChange);
    };

    init();

    return {
        destroy
    };
};
